import React from 'react';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { StandardStatus } from '@zoocasa/go-search';
import { Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { getStatusLabelKey } from 'types/reso';
import { getListingMlsStatus } from 'ui/listing-status/listing-mls-status';
import Tooltip from 'components/atomic/molecules/tooltip';
import { Info } from 'lucide-react';

export const LISTING_STATUS_SIZES = {
  DEFAULT: 'default',
  SMALL: 'small',
} as const;

export type ListingStatusSize = typeof LISTING_STATUS_SIZES[keyof typeof LISTING_STATUS_SIZES];

export type ListingStatusProps = {
  status: StandardStatus | null;
  isRental?: boolean;
  size?: ListingStatusSize;
  className?: string | undefined;
  providerId?: number | null;
  mlsStatus?: string | null;
  showMlsStatusTooltip?: boolean;
};

export default function ListingStatus({ status, isRental, size = LISTING_STATUS_SIZES.DEFAULT, className, providerId, mlsStatus, showMlsStatusTooltip = false }: ListingStatusProps) {
  const { t } = useTranslation(Namespaces.COMMON);

  if (!status) {
    return null;
  }

  const statusLabelKey = getStatusLabelKey(status, isRental);
  const statusClass = getStatusClass(status);
  const sizeClass = size === LISTING_STATUS_SIZES.SMALL ? styles['size-small'] : '';
  const mlsLabel = getListingMlsStatus({ providerId, mlsStatus });
  const standardStatusLabel = t(statusLabelKey);
  const pillText = showMlsStatusTooltip && mlsLabel ? standardStatusLabel : mlsLabel || standardStatusLabel;
  const statusPill = (
    <div className={`${styles.component} ${statusClass} ${sizeClass} ${className ?? ''}`}>
      <p data-testid={listingPageModalIds.listingStatus}>{pillText}</p>
    </div>
  );

  if (!showMlsStatusTooltip || !mlsLabel) {
    return statusPill;
  }

  return (
    <Tooltip content={mlsLabel} ariaLabel={mlsLabel} tooltipSx={{ maxWidth: '20rem' }}>
      <span className={styles['tooltip-trigger']}>
        {statusPill}
        <Info className={styles['info-icon']} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}

/**
 * Maps a listing status to the appropriate CSS class name for styling.
 *
 * This function determines the correct CSS module class based on the listing status
 * to apply appropriate visual styling (colors, borders, etc.) to the status badge.
 * Multiple statuses may map to the same CSS class for consistent visual grouping.
 *
 * @param status the standard status of the listing from the StandardStatus enum
 * @returns A CSS module class name string that corresponds to the status styling
 *
 * @example
 * ```tsx
 * const statusClass = getStatusClass(StandardStatus.STANDARD_STATUS_ACTIVE);
 * // Returns: styles['active'] (applies active status styling)
 *
 * const statusClass = getStatusClass(StandardStatus.STANDARD_STATUS_HOLD);
 * // Returns: styles['inactive'] (applies inactive status styling)
 * ```
 *
 * @remarks
 * - Active and Active Under Contract statuses both use 'active' styling
 * - Hold, Withdrawn, and Pending statuses all use 'inactive' styling
 * - Each status maps to one of: active, closed, expired, canceled, inactive, or other
 * - The returned class name corresponds to CSS rules in style.module.scss
 * - Falls back to 'other' styling if an unrecognized status is provided
 */
function getStatusClass(status: StandardStatus) {
  let statusClass: string;
  switch (status) {
    case StandardStatus.STANDARD_STATUS_ACTIVE:
    case StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT:
      statusClass = styles['active'];
      break;
    case StandardStatus.STANDARD_STATUS_CLOSED:
      statusClass = styles['closed'];
      break;
    case StandardStatus.STANDARD_STATUS_EXPIRED:
      statusClass = styles['expired'];
      break;
    case StandardStatus.STANDARD_STATUS_CANCELED:
      statusClass = styles['canceled'];
      break;
    case StandardStatus.STANDARD_STATUS_HOLD:
    case StandardStatus.STANDARD_STATUS_WITHDRAWN:
    case StandardStatus.STANDARD_STATUS_PENDING:
      statusClass = styles['inactive'];
      break;
    default:
      statusClass = styles['other'];
      break;
  }
  return statusClass;
}